import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../api";

const MS_DAY = 24 * 60 * 60 * 1000;

const dueClass = (dueStr, completed) => {
  if (completed) return "bg-green-100 text-green-700";
  if (!dueStr) return "bg-gray-100 text-gray-600";
  const due = new Date(dueStr + "T00:00:00");
  if (isNaN(due.getTime())) return "bg-gray-100 text-gray-600";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = due.getTime() - today.getTime();
  if (diff < 0) return "bg-red-100 text-red-700";
  if (diff <= 7 * MS_DAY) return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-700";
};

const fmtDue = (dueStr) =>
  new Date(dueStr + "T00:00:00").toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const EditRequirementModal = ({ requirement, onClose, onSave }) => {
  const [description, setDescription] = useState(requirement.description || "");
  const [dueDate, setDueDate] = useState(requirement.due_date || "");
  const [resolution, setResolution] = useState(requirement.resolution || "");
  const [completed, setCompleted] = useState(!!requirement.completed);
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = description.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onSave({
        description: trimmed,
        due_date: dueDate || null,
        resolution: resolution || null,
        completed,
      });
      onClose();
    } catch {
      // toast handled in onSave
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50"
      data-testid="requirement-edit-modal"
    >
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Requirement</h3>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Requirement</label>
            <input
              type="text"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="requirement-edit-description-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due date (optional)</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="requirement-edit-due-date-input"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              How was this requirement met?
            </label>
            <textarea
              rows="4"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              data-testid="requirement-resolution-input"
              placeholder="Add a description of how this requirement was fulfilled..."
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={completed}
              onChange={(e) => setCompleted(e.target.checked)}
              data-testid="requirement-completed-checkbox"
              className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <span className="text-sm font-medium text-gray-700">Mark as completed</span>
          </label>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="requirement-edit-cancel"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="requirement-edit-save"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RequirementsList = ({ account, onChange }) => {
  const [requirements, setRequirements] = useState([]);
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const collapseStorageKey = `accountz.reqCollapsed.${account.id}`;
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(collapseStorageKey) || "{}") || {};
    } catch {
      return {};
    }
  });

  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(collapseStorageKey, JSON.stringify(next));
      } catch {
        /* ignore quota errors */
      }
      return next;
    });

  // Re-hydrate collapsed state when the selected account changes
  useEffect(() => {
    try {
      setCollapsedGroups(JSON.parse(localStorage.getItem(collapseStorageKey) || "{}") || {});
    } catch {
      setCollapsedGroups({});
    }
  }, [account.id, collapseStorageKey]);

  const fetchRequirements = async () => {
    try {
      const res = await axios.get(`${API}/accounts/${account.id}/requirements`);
      setRequirements(res.data);
    } catch (error) {
      console.error("Failed to fetch requirements:", error);
    }
  };

  useEffect(() => {
    fetchRequirements();
  }, [account.id]);

  const addRequirement = async (e) => {
    e.preventDefault();
    const text = description.trim();
    if (!text) return;
    setSaving(true);
    try {
      await axios.post(`${API}/accounts/${account.id}/requirements`, {
        description: text,
        due_date: dueDate || null,
      });
      setDescription("");
      setDueDate("");
      await fetchRequirements();
      if (onChange) onChange();
    } catch (error) {
      console.error("Failed to add requirement:", error);
      toast.error("Failed to add requirement");
    } finally {
      setSaving(false);
    }
  };

  const deleteRequirement = async (reqId) => {
    try {
      await axios.delete(`${API}/accounts/${account.id}/requirements/${reqId}`);
      await fetchRequirements();
      if (onChange) onChange();
    } catch (error) {
      console.error("Failed to delete requirement:", error);
      toast.error("Failed to delete requirement");
    }
  };

  const updateRequirement = async (reqId, payload) => {
    try {
      await axios.put(`${API}/accounts/${account.id}/requirements/${reqId}`, payload);
      await fetchRequirements();
      if (onChange) onChange();
    } catch (error) {
      console.error("Failed to update requirement:", error);
      toast.error("Failed to update requirement");
      throw error;
    }
  };

  const sorted = [...requirements].sort((a, b) => {
    // Pure chronological (earliest first). Undated items go last.
    if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return (a.created_at || "").localeCompare(b.created_at || "");
  });

  // Group by year of due_date (undated bucket at the end)
  const grouped = [];
  const bucketByKey = new Map();
  const getBucket = (key, label) => {
    let bucket = bucketByKey.get(key);
    if (!bucket) {
      bucket = { key, label, items: [] };
      bucketByKey.set(key, bucket);
      grouped.push(bucket);
    }
    return bucket;
  };
  sorted.forEach((req) => {
    if (req.due_date) {
      const year = req.due_date.slice(0, 4);
      getBucket(year, year).items.push(req);
    } else {
      getBucket("undated", "Undated").items.push(req);
    }
  });

  return (
    <div className="mb-6" data-testid="requirements-section">
      <h3 className="text-sm font-medium text-gray-900 mb-3">
        Requirements ({requirements.length})
      </h3>

      <form
        onSubmit={addRequirement}
        className="flex flex-col sm:flex-row gap-2 mb-3"
        data-testid="requirement-add-form"
      >
        <input
          type="text"
          required
          placeholder="Add a requirement..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="requirement-description-input"
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          data-testid="requirement-due-date-input"
          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="submit"
          disabled={saving}
          data-testid="requirement-add-button"
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50"
        >
          Add
        </button>
      </form>

      {sorted.length === 0 ? (
        <div
          className="text-center py-6 text-gray-500 text-sm border border-dashed border-gray-200 rounded-lg"
          data-testid="requirements-empty"
        >
          No requirements yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="requirements-list">
          {grouped.map((bucket) => {
            const collapsed = !!collapsedGroups[bucket.key];
            return (
              <div key={bucket.key} data-testid={`requirements-year-${bucket.key}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(bucket.key)}
                  data-testid={`requirements-year-toggle-${bucket.key}`}
                  aria-expanded={!collapsed}
                  className="w-full flex items-center gap-2 mb-2 text-left group"
                >
                  <svg
                    className={`w-3.5 h-3.5 text-gray-500 transition-transform ${collapsed ? "-rotate-90" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide group-hover:text-gray-700">
                    {bucket.label}
                  </h4>
                  <span className="text-xs text-gray-400">({bucket.items.length})</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </button>
                {!collapsed && (
                  <ul className="space-y-2">
                    {bucket.items.map((req) => (
                      <li
                        key={req.id}
                        data-testid={`requirement-${req.id}`}
                        className={`flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-white ${
                          req.completed ? "opacity-70" : ""
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm text-gray-900 truncate ${
                              req.completed ? "line-through text-gray-500" : ""
                            }`}
                          >
                            {req.description}
                          </p>
                          {req.resolution && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2" data-testid={`requirement-resolution-${req.id}`}>
                              {req.resolution}
                            </p>
                          )}
                        </div>
                        {req.completed && (
                          <span
                            data-testid={`requirement-completed-${req.id}`}
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 flex-shrink-0"
                          >
                            Completed
                          </span>
                        )}
                        {req.due_date && (
                          <span
                            data-testid={`requirement-due-${req.id}`}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${dueClass(req.due_date, req.completed)} flex-shrink-0`}
                          >
                            {fmtDue(req.due_date)}
                          </span>
                        )}
                        <button
                          onClick={() => setEditing(req)}
                          data-testid={`requirement-edit-${req.id}`}
                          className="text-gray-500 hover:text-blue-600 flex-shrink-0"
                          title="Edit requirement"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => deleteRequirement(req.id)}
                          data-testid={`requirement-delete-${req.id}`}
                          className="text-red-500 hover:text-red-700 flex-shrink-0"
                          title="Delete requirement"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2"
                            />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <EditRequirementModal
          requirement={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => updateRequirement(editing.id, payload)}
        />
      )}
    </div>
  );
};

export default RequirementsList;
